import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Lightbulb, Printer, RotateCcw, Shuffle, Volume2 } from "lucide-react";
import { useLang, loc, useContent } from "@/lib/content";
import { api, scrollToId } from "@/lib/api";
import { speak } from "@/lib/tts";
import { CATS, FLASHCARDS, FC_UI } from "@/lib/flashcards";

const LS_KEY = "civica-learned";

const loadLearned = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(LS_KEY) || "[]"));
  } catch (e) {
    return new Set();
  }
};

export default function Flashcards() {
  const { lang } = useLang();
  const { content } = useContent();
  const [cat, setCat] = useState("todas");
  const [order, setOrder] = useState(() => FLASHCARDS.map((_, i) => i));
  const [pos, setPos] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [learned, setLearned] = useState(loadLearned);
  const [contexts, setContexts] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api("/study/contexts").then(setContexts).catch(() => {});
  }, []);

  const deck = useMemo(
    () => (cat === "todas" ? order : order.filter((i) => FLASHCARDS[i].cat === cat)),
    [cat, order]
  );

  const safePos = Math.min(pos, Math.max(deck.length - 1, 0));
  const card = deck[safePos] !== undefined ? FLASHCARDS[deck[safePos]] : null;
  const isLearned = card ? learned.has(card.n) : false;

  const shuffle = () => {
    const arr = [...order];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setOrder(arr);
    setPos(0);
    setFlipped(false);
  };

  const go = (dir) => {
    const nextPos = safePos + dir;
    if (nextPos < 0 || nextPos >= deck.length) return;
    setPos(nextPos);
    setFlipped(false);
  };

  const changeCat = (key) => {
    setCat(key);
    setPos(0);
    setFlipped(false);
  };

  const toggleKnow = (e) => {
    e.stopPropagation();
    const next = new Set(learned);
    if (next.has(card.n)) next.delete(card.n);
    else next.add(card.n);
    setLearned(next);
    localStorage.setItem(LS_KEY, JSON.stringify([...next]));
  };

  const resetProgress = () => {
    setLearned(new Set());
    localStorage.setItem(LS_KEY, "[]");
  };

  const otherLang = lang === "es" ? "en" : "es";
  const catOf = (key) => CATS.find((c) => c.key === key);
  const ctx = card ? contexts[String(card.n)] : null;

  const play = async (text, key) => {
    setBusy(key);
    try {
      const a = await speak(text);
      a.onended = () => setBusy("");
    } catch (e) {
      setBusy("");
    }
  };

  return (
    <section id="flashcards" className="bg-paperdeep py-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">{loc(FC_UI.overline, lang)}</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {loc(FC_UI.title, lang)}
            <span className="italic text-heritage">{loc(FC_UI.titleAccent, lang)}</span>
          </h2>
          <p className="mt-4 text-inkbody">{loc(FC_UI.intro, lang)}</p>
        </motion.div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          <button
            data-testid="flash-cat-todas"
            onClick={() => changeCat("todas")}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${cat === "todas" ? "bg-navy text-paper" : "border border-line bg-white text-navy hover:border-navy/40"}`}
          >
            {loc(FC_UI.catAll, lang)} · {FLASHCARDS.length}
          </button>
          {CATS.map((c) => {
            const count = FLASHCARDS.filter((f) => f.cat === c.key).length;
            return (
              <button
                key={c.key}
                data-testid={`flash-cat-${c.key}`}
                onClick={() => changeCat(c.key)}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${cat === c.key ? "bg-navy text-paper" : "border border-line bg-white text-navy hover:border-navy/40"}`}
              >
                {loc(c.label, lang)} · {count}
              </button>
            );
          })}
        </div>

        <div className="mx-auto mt-8 max-w-2xl">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="font-mono text-xs text-mutedtext" data-testid="flash-progress">
              {loc(FC_UI.progress, lang)} {learned.size} {loc(FC_UI.of, lang)}
            </p>
            <button
              data-testid="flash-shuffle"
              onClick={shuffle}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-1.5 text-xs font-bold text-navy hover:border-navy/40"
            >
              <Shuffle size={13} /> {loc(FC_UI.shuffle, lang)}
            </button>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-line/60">
            <motion.div className="h-full rounded-full bg-emeraldsucc" animate={{ width: `${(learned.size / FLASHCARDS.length) * 100}%` }} transition={{ duration: 0.4 }} data-testid="flash-progress-bar" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {card && (
            <motion.div
              key={cat + "-" + safePos + "-" + card.n}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35 }}
              className="mx-auto mt-8 max-w-2xl"
            >
              <div
                data-testid="flashcard"
                onClick={() => setFlipped((v) => !v)}
                className={`flip-card h-[30rem] w-full cursor-pointer select-none sm:h-[32rem] ${flipped ? "flipped" : ""}`}
              >
                <div className="flip-inner relative h-full w-full">
                  <div className="flip-face absolute inset-0 overflow-hidden rounded-3xl border border-line bg-white shadow-xl">
                    <div className="relative h-52 sm:h-56">
                      <img src={card.img} alt={loc(card.q, lang)} className="h-full w-full object-cover" data-testid="flashcard-img" />
                      <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-navy/10" />
                      <span className="absolute left-4 top-4 rounded-full bg-navy/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-paper">
                        {loc(FC_UI.num, lang)} {card.n}
                      </span>
                      <span className="absolute right-4 top-4 rounded-full border border-brass/40 bg-white/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-heritage">
                        {loc(catOf(card.cat).label, lang)}
                      </span>
                    </div>
                    <div className="flex h-[calc(100%-13rem)] flex-col justify-between p-6 sm:h-[calc(100%-14rem)]">
                      <p className="font-display text-2xl font-semibold leading-snug text-navy sm:text-3xl">{loc(card.q, lang)}</p>
                      {ctx && (
                        <p className="mt-2 flex items-start gap-1.5 text-[11px] italic leading-snug text-mutedtext" data-testid="flashcard-context-front">
                          <Lightbulb size={12} className="mt-0.5 shrink-0 text-brass" /> {loc(ctx, lang)}
                        </p>
                      )}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <button
                            data-testid="flash-audio-es"
                            onClick={(e) => {
                              e.stopPropagation();
                              play(card.q.es, "q-es");
                            }}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${busy === "q-es" ? "border-heritage bg-heritage/10 text-heritage" : "border-line text-navy hover:border-navy/40"}`}
                          >
                            <Volume2 size={12} /> ES
                          </button>
                          <button
                            data-testid="flash-audio-en"
                            onClick={(e) => {
                              e.stopPropagation();
                              play(card.q.en, "q-en");
                            }}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${busy === "q-en" ? "border-heritage bg-heritage/10 text-heritage" : "border-line text-navy hover:border-navy/40"}`}
                          >
                            <Volume2 size={12} /> EN
                          </button>
                        </div>
                        <span className="font-mono text-[11px] text-mutedtext">
                          {safePos + 1} / {deck.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flip-face flip-back absolute inset-0 flex flex-col rounded-3xl border border-linelight bg-navy p-7 text-paper shadow-2xl">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-brass">{loc(FC_UI.answer, lang)}</span>
                      <span className="font-mono text-[10px] text-paper/50">{loc(FC_UI.num, lang)} {card.n}</span>
                    </div>
                    <div className="mt-5 flex-1 overflow-y-auto">
                      <p className="text-lg font-semibold leading-relaxed sm:text-xl" data-testid="flashcard-answer">{loc(card.a, lang)}</p>
                      <p className="mt-3 text-sm leading-relaxed text-paper/55">{loc(card.a, otherLang)}</p>
                      {ctx && (
                        <div className="mt-4 rounded-xl border border-brass/30 bg-brass/10 p-3.5" data-testid="flashcard-context">
                          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-brass">{lang === "es" ? "Contexto" : "Context"}</p>
                          <p className="mt-1 text-[12px] leading-relaxed text-paper/85">{loc(ctx, lang)}</p>
                          <p className="mt-1 text-[11px] leading-relaxed text-paper/50">{loc(ctx, otherLang)}</p>
                        </div>
                      )}
                    </div>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-paper/40">{loc(FC_UI.multi, lang)}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  data-testid="flash-prev"
                  onClick={() => go(-1)}
                  disabled={safePos === 0}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-5 py-3 text-xs font-bold text-navy transition-all hover:border-navy/40 disabled:opacity-40"
                >
                  <ArrowLeft size={14} /> {loc(FC_UI.prev, lang)}
                </button>
                <button
                  data-testid="flash-know"
                  onClick={toggleKnow}
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-3 text-xs font-bold transition-all ${
                    isLearned ? "bg-emeraldsucc text-paper shadow-lg shadow-emeraldsucc/25" : "border border-emeraldsucc/40 bg-emeraldsucc/10 text-emeraldsucc hover:bg-emeraldsucc hover:text-paper"
                  }`}
                >
                  <CheckCircle2 size={14} /> {isLearned ? loc(FC_UI.known, lang) : loc(FC_UI.know, lang)}
                </button>
                <button
                  data-testid="flash-next"
                  onClick={() => go(1)}
                  disabled={safePos >= deck.length - 1}
                  className="inline-flex items-center gap-1.5 rounded-full bg-heritage px-5 py-3 text-xs font-bold text-paper transition-all hover:bg-heritagedark disabled:opacity-40"
                >
                  {loc(FC_UI.next, lang)} <ArrowRight size={14} />
                </button>
              </div>

              <div className="mt-5 flex items-center justify-center gap-5 text-[11px] font-semibold text-mutedtext">
                <button data-testid="flash-reset" onClick={resetProgress} className="inline-flex items-center gap-1.5 hover:text-heritage">
                  <RotateCcw size={12} /> {loc(FC_UI.reset, lang)}
                </button>
                <button data-testid="flash-print" onClick={() => window.print()} className="inline-flex items-center gap-1.5 hover:text-heritage">
                  <Printer size={12} /> {lang === "es" ? "Imprimir mazo" : "Print deck"}
                </button>
                {learned.size === FLASHCARDS.length && <span className="font-bold text-emeraldsucc" data-testid="flash-complete">{loc(FC_UI.complete, lang)}</span>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14 text-center"
        >
          <a
            href="#inscripcion"
            data-testid="flashcards-cta"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#inscripcion");
            }}
            className="rounded-full bg-heritage px-8 py-4 text-sm font-bold text-paper shadow-xl shadow-heritage/25 transition-all hover:-translate-y-0.5 hover:bg-heritagedark"
          >
            {loc(FC_UI.cta, lang)}
          </a>
        </motion.div>

        {createPortal(
          <div className="print-overlay" data-testid="flash-print-overlay">
            <div className="deck-head">
              <b>{content.brand_name || "CÍVICA"} · {lang === "es" ? "Las 128 preguntas oficiales — mazo imprimible (2026)" : "The 128 official questions — printable deck (2026)"}</b>
              <span>{lang === "es" ? "Corta y practica" : "Cut and practice"}</span>
            </div>
            <div className="deck-grid">
              {FLASHCARDS.map((f) => (
                <div key={f.n} className="deck-card">
                  <b>{f.n}. {loc(f.q, lang)}</b>
                  <span className="a">{loc(f.a, lang)}</span>
                  {contexts[String(f.n)] && <div className="c">{loc(contexts[String(f.n)], lang)}</div>}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    </section>
  );
}
